import { Typography } from '@ywwwtseng/tma-react-kit';
import { Input, useI18n } from '@ywwwtseng/react-kit';
import { ChevronDown } from 'lucide-react';
import { Select } from '@/components/ui/Select';
import { SelectChat } from '@/components/common/SelectChat';
import {
  GIFT_TYPES,
  CLAIM_TYPES,
  DEFAULT_GIFT_METADATA,
} from '@/constants';
import type { GiftMetadataWithoutSettings } from '@/types';
import { TaskGiftApiGuide } from './TaskGiftApiGuide';

export function GiftClaimTypeAndMetadataInputs({
  type,
  claimType,
  metadata,
  onChange,
}: {
  type: GIFT_TYPES;
  claimType: CLAIM_TYPES;
  metadata: GiftMetadataWithoutSettings;
  onChange: ({
    claim_type,
    metadata,
  }: {
    claim_type: CLAIM_TYPES;
    metadata: GiftMetadataWithoutSettings;
  }) => void;
}) {
  const { t } = useI18n();
  const options = Object.entries(DEFAULT_GIFT_METADATA[type]).map(
    ([claim_type]) => ({
      key: claim_type,
      name: t(`claim_type.${claim_type}`),
    })
  );

  const keys = Object.keys(metadata);

  return (
    <div className="v-stack w-full gap-2 !items-start">
      <div className="w-full flex items-center justify-between">
        <div className="flex items-center justify-start w-full gap-2">
          <Typography size="2" i18n={`claim_settings_title.${type}`} />
          {claimType === CLAIM_TYPES.API && <TaskGiftApiGuide />}
        </div>
        <Select
          trigger={(
            <div className="flex gap-1 bg-form-element rounded-full text-sm p-2 pl-4 whitespace-nowrap">
              <Typography size="2" className={claimType ? 'text-foreground' : 'text-placeholder'}>{t(`claim_type.${claimType}`)}</Typography>
              <ChevronDown width={20} height={20} />
            </div>
          )}
          options={options}
          value={claimType}
          onChange={(claim_type) => {
            onChange({
              claim_type: claim_type as CLAIM_TYPES,
              metadata:
                DEFAULT_GIFT_METADATA[type][
                claim_type as keyof (typeof DEFAULT_GIFT_METADATA)[GIFT_TYPES]
                ],
            });
          }}
        />
      </div>

      {keys.length > 0 && (
        <div className="v-stack w-full gap-2">
          {keys.map((key) => (
            key === 'chat_id' ? (
              <SelectChat
                key={key}
                hidePrivateChats
                value={metadata[key as keyof typeof metadata] as string}
                onChange={(chat_id) => {
                  onChange({
                    claim_type: claimType,
                    metadata: {
                      ...metadata,
                      [key]: chat_id,
                    },
                  });
                }}
              />
            ) : (
              <div className="w-full" key={key}>
                <Input
                  type="text"
                  placeholder={t(`claim_settings_placeholder.${key}`)}
                  className="w-full p-2.5 text-right bg-modal rounded-lg text-xs"
                  value={metadata[key as keyof typeof metadata] as string}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    onChange({
                      claim_type: claimType,
                      metadata: {
                        ...metadata,
                        [key]: e.target.value,
                      },
                    });
                  }}
                />
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
